<template>
  <ul v-show="expand">
    <li class="item">
      <div
        v-click-outside="branchUnselected"
        class="row"
        :class="{ 'selected': isSelected }"
        @mouseover="schema.hasOwnProperty('dataType') && onBranchHover(true, `schema-${schemaPath}`, schema)"
        @mouseleave="schema.hasOwnProperty('dataType') && onBranchHover(false, `schema-${schemaPath}`)"
        @click="branchSelected()"
      >
        <i
          v-if="showArrows"
          class="arrow"
          :class="arrowClass"
          @click="handleExpand"
        />
        <i
          v-if="schema.icon"
          class="icon"
          :class="schema.icon"
          :style="`color: ${setIconColor(schema.iconColor)}`"
        />
        <span
          :ref="`schema-${schemaPath}`"
          class="title"
          @dblclick="!schema.excludeFromPath && getRoute(schema)"
        >
          {{ schema.title }}
        </span>
      </div>
      <template v-if="loading">
        <Spin fix>
          <Icon
            type="ios-loading"
            size="18"
            class="loading-icon"
          />
          <div>Loading</div>
        </Spin>
      </template>
      <template v-else-if="!loading && schema.schemaView">
        <Tree-node
          v-for="(item, index) in schema.schemaView"
          :key="index"
          :schema="item"
          :schema-path="`${schemaPath}-${index}`"
          :arrows-type="arrowsType"
          :expand="schema.expand"
          @schemaPath="createPath"
          @showColumnDetails="showColumnDetails"
        />
      </template>
    </li>
  </ul>
</template>

<script>
import vClickOutside from 'v-click-outside'
export default {
  name: 'TreeNode',

  directives: {
    clickOutside: vClickOutside.directive
  },

  props: {
    schema: {
      type: Object,
      default () {
        return {}
      }
    },
    schemaPath: {
      type: String
    },
    arrowsType: {
      type: String
    },
    expand: {
      type: Boolean
    }
  },

  data () {
    return {
      arrowCollapsed: '',
      arrowExpanded: '',
      showArrows: true,
      loading: false,
      isSelected: false
    }
  },

  computed: {
    arrowClass () {
      return this.schema.expand ? this.arrowExpanded : this.arrowCollapsed
    }
  },

  mounted () {
    this.schema.expand = this.schema.expand || false

    if (!this.schema.schemaView) {
      this.showArrows = false
    } else {
      switch (this.arrowsType) {
        case 'caret':
          this.arrowCollapsed = 'fas fa-caret-right'
          this.arrowExpanded = 'fas fa-caret-down'
          break
        case 'chevron':
          this.arrowCollapsed = 'fas fa-chevron-right'
          this.arrowExpanded = 'fas fa-chevron-down'
          break
        case 'folder':
          this.arrowCollapsed = 'far fa-folder'
          this.arrowExpanded = 'far fa-folder-open'
          break
        case 'square':
          this.arrowCollapsed = 'far fa-plus-square'
          this.arrowExpanded = 'far fa-minus-square'
          break
        default:
          this.arrowCollapsed = 'fas fa-angle-right'
          this.arrowExpanded = 'fas fa-angle-down'
          break
      }
    }
  },

  methods: {
    /**
       * Expands or collaps an item (if it has a schemaView)
       */
    handleExpand () {
      this.loading = this.schema.schemaView && !this.schema.schemaView.length

      if (this.loading) {
        this.$set(this.schema, 'schemaView', [...this.schema.schemaContent])
        this.$set(this.schema, 'schemaContent', [])
        this.loading = false
      }

      this.schema.expand = !this.schema.expand
    },

    /**
       * Triggers the function to create a schema path from selected item.
       * @param {Object} item
       */
    getRoute (item) {
      this.$emit('schemaPath', `"${item.title}"`)
    },

    /**
       * Sets the schema path to be sent to the parent component.
       * @param {String} title
       */
    createPath (title) {
      const newPath = this.schema.excludeFromPath ? title : `"${this.schema.title}".${title}`
      this.$emit('schemaPath', newPath)
    },

    /**
       * Sets the level icon color.
       */
    setIconColor (iconColor = '#444a57') {
      return iconColor
    },

    /**
       * Specifies that a branch has been selected.
       */
    branchSelected () {
      this.isSelected = true
    },

    /**
       * Specifies that a focused branch has been unselected.
       * @param {Object} event
       */
    branchUnselected () {
      this.isSelected = false
    },

    /**
       * Sets properties to send to parent in order to display the column details on a Preview window
       * @param {Boolean} status
       * @param {String} schemaRef Contains the "span" ref in order to calculate its current position.
       * @param {Object} schema Schema content to be displayed
       */
    onBranchHover (status, schemaRef, schema = {}) {
      const schemaPosition = this.$refs[schemaRef].getBoundingClientRect().top
      this.$emit('showColumnDetails', status, schemaPosition, schema)
    },

    /**
       * Sends the column information to the parent in order to be displayed.
       * @param {Boolean} status
       * @param {Number} schemaPosition Current position of the hovered column name.
       * @param {Object} schemaData Schema content to be displayed
       */
    showColumnDetails (status, schemaPosition, schemaData) {
      this.$emit('showColumnDetails', status, schemaPosition, schemaData)
    }
  }
}
</script>

<style scoped>
  .loading-icon {
    animation: ani-demo-spin 1s linear infinite;
  }

  .item {
    user-select: none;
  }

  .row {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    padding: 2px 0;
    margin: 0 -4px;
  }

  .arrow,
  .icon {
    margin-left: 4px;
    padding-right: 7px;
  }

  .arrow {
    cursor: pointer;
  }

  .title {
    margin-right: 4px;
    display: block;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .row:hover {
    border-radius: 4px;
    background-color: var(--color-light-gray)
  }

  .selected,
  .selected:hover {
    border-radius: 4px;
    background-color: var(--color-gray-button)
  }
</style>
